import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { useUser } from './UserContext';
import { useTiers } from './TiersContext';
import WelcomeCard from './WelcomeCard';
import SectionCard from './SectionCard';

// Display-only config (prices, trial) — limits come from the API via useTiers().
const TIER_DISPLAY = [
  { key: 'free',    name: 'Free',    price: 'Free',       regular: null,  trial: false },
  { key: 'starter', name: 'Starter', price: '$5',         regular: '$8',  trial: true  },
  { key: 'small',   name: 'Small',   price: '$10',        regular: '$15', trial: true  },
  { key: 'medium',  name: 'Medium',  price: '$25',        regular: '$30', trial: true  },
  { key: 'large',   name: 'Large',   price: 'Contact us', regular: null,  trial: false },
];

function formatGalleryPhotos(tierData) {
  if (!tierData) return '—';
  if (tierData.maxGalleryPhotos === null) return 'Custom';
  const suffix = tierData.maxGalleries > 1 ? ' per gallery' : '';
  return `${tierData.maxGalleryPhotos}${suffix}`;
}

const PricingTable = ({ tiers }) => {
  const rows = [
    { label: 'Price / month',             render: (t, _td) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {t.name === 'Large' ? <Link to="/contact" style={{ color: 'inherit' }}>{t.price}</Link> : t.price}
          </Typography>
          {t.regular && (
            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
              {t.regular}/mth
            </Typography>
          )}
        </Box>
      )},
    { label: 'Max members',               render: (_t, td) => td ? (td.maxMembers ?? 'Custom') : null },
    { label: 'Concurrent contests',       render: (_t, td) => td ? (td.maxContests ?? 'Custom') : null },
    { label: 'Contest photos per member', render: (_t, td) => td ? (td.contestPhotosPerMember ?? 'Custom') : null },
    { label: 'Concurrent galleries',      render: (_t, td) => td ? (td.maxGalleries ?? 'Custom') : null },
    { label: 'Gallery photos',            render: (_t, td) => td ? formatGalleryPhotos(td) : null },
    { label: '3-day free trial',          render: (t, _td) => t.trial ? '✓' : '—' },
  ];

  return (
    <Box sx={{ position: 'relative', maxWidth: '1200px', width: '100%', marginBottom: '40px' }}>
      {!tiers && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, bgcolor: 'action.disabledBackground' }}>
          <CircularProgress />
        </Box>
      )}
    <TableContainer component={Paper} elevation={3} sx={{ maxWidth: '1200px', width: '100%' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            {TIER_DISPLAY.map(t => (
              <TableCell key={t.name} align="center" sx={{ fontWeight: 'bold' }}>{t.name}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.label}>
              <TableCell sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}>{row.label}</TableCell>
              {TIER_DISPLAY.map(t => (
                <TableCell key={t.name} align="center">{row.render(t, tiers?.[t.key])}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

const Pricing = () => {
  const { user } = useUser();
  const tiers = useTiers();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      flex: 1, // Takes up remaining space
      paddingTop: '50px',
      paddingBottom: '50px',
    }}>
      <div style={{position: 'absolute', left: '0px', top: '0px', width:'100%', opacity: '10%', pointerEvents: 'none' }}>
        <img src='/mascot.png' alt=''/>
      </div>

      {!user && (
        <WelcomeCard />
      )}

      <SectionCard
        headline="Pricing"
        text="Snapperish pricing has five tiers: Free Club, Starter Club, Small Club, Medium Club, Large Club. Paid subscriptions are free for 3 days, after which you will be charged the monthly fee. You can cancel at any time, including within the trial period to avoid getting charged."
        index={0}
      />

      <PricingTable tiers={tiers} />

      <SectionCard
        headline="Free Club"
        text={`The Snapperish Free Club is perfect for small groups wanting to try Snapperish at no cost. It's completely free — no subscription, no credit card required. You are limited to ${tiers?.free?.maxMembers ?? 5} members, ${tiers?.free?.maxContests ?? 1} contest at a time, and ${tiers?.free?.contestPhotosPerMember ?? 5} photos per person.`}
        index={1}
      />

      <SectionCard
        headline="Starter Club"
        text={`The Snapperish Starter Club is perfect for small groups just getting started. It costs $8 per month (currently just $5/mth - save $3!) and can be canceled at any time. You are limited to ${tiers?.starter?.maxMembers ?? 25} members, ${tiers?.starter?.maxContests ?? 1} contest at a time and ${tiers?.starter?.contestPhotosPerMember ?? 5} photos per person. Photo archives can be downloaded by the Owner and Contest Administrators and are deleted when a new contest begins.`}
        index={2}
      />

      <SectionCard
        headline="Small Club"
        text={`The Snapperish Small Club is ideal for clubs with up to ${tiers?.small?.maxMembers ?? 99} members. It costs $15 per month (currently just $10/mth - save $5!) and can be canceled at any time. You are limited to ${tiers?.small?.maxContests ?? 1} contest at a time and ${tiers?.small?.contestPhotosPerMember ?? 10} photos per person. Photo archives can be downloaded by the Owner and Contest Administrators and are deleted when a new contest begins.`}
        index={3}
      />

      <SectionCard
        headline="Medium Club"
        text={`The Snapperish Medium Club is ideal for clubs with between ${(tiers?.small?.maxMembers ?? 99) + 1} and ${tiers?.medium?.maxMembers ?? 250} members. It costs $30 per month (currently just $25/mth - save $5!) and can be canceled at any time. You are limited to ${tiers?.medium?.maxContests ?? 2} contests at a time and ${tiers?.medium?.contestPhotosPerMember ?? 15} photos per person. Photo archives can be downloaded by the Owner and Contest Administrators. Older contest photos are deleted when a third contest begins.`}
        index={4}
      />

      <SectionCard
        headline="Large Clubs"
        text="Snapperish wants to support your Large Club with special pricing and options. If you have more than 250 members, contact us for special subscription pricing, and we can accomodate any number of members, contests and photo retension. Generally the cost will be about $15 per 250 members per retained or active photo contest."
        index={5}
      />
    </Box>
  );
};

export default Pricing;
